<!-- eslint-disable -->
<script setup>
/* eslint-disable */
import { computed, inject } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const realData = inject('imudeiRealData');
const moduleKey = computed(() => 'brokers');
const screen = computed(() => 'detail');
const runtimeModel = useImudeiRuntimeModel({
  realData,
  module: moduleKey,
  screen,
});

const firstValue = (value, paths, fallback = '') => {
  const found = paths
    .map(path => path.split('.').reduce((item, key) => item?.[key], value))
    .find(item => item !== undefined && item !== null && item !== '');

  return found ?? fallback;
};

const imageUrl = value => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';

  return firstValue(value, ['url', 'file_url', 'image_url', 'avatar_url']);
};

const initialsFor = value =>
  String(value || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

const detail = computed(() => runtimeModel.detail.value);
const broker = computed(() => detail.value?.source || {});
const profile = computed(() => realData.data.value?.profile || {});
const brokerName = computed(
  () => detail.value?.title || 'Corretor não disponibilizado'
);
const brokerRole = computed(
  () => detail.value?.subtitle || 'Função não disponibilizada'
);
const brokerId = computed(() => detail.value?.id || '—');
const brokerStatus = computed(
  () => detail.value?.status || 'Status não disponibilizado'
);
const brokerEmail = computed(() =>
  firstValue(broker.value, ['email', 'user.email'], '')
);
const brokerAvatar = computed(() =>
  imageUrl(
    firstValue(broker.value, [
      'avatar_url',
      'thumbnail',
      'user.avatar_url',
      'user.thumbnail',
    ])
  )
);
const brokerListings = computed(() =>
  firstValue(broker.value, ['active_listings'], '—')
);
const brokerVisibility = computed(() => {
  const isPublic = firstValue(broker.value, ['public'], null);
  if (isPublic === true) return 'Perfil público';
  if (isPublic === false) return 'Perfil privado';
  return 'Visibilidade não disponibilizada';
});
const agencyName = computed(() =>
  firstValue(
    profile.value,
    ['nome_fantasia', 'razao_social'],
    'Imobiliária não disponibilizada'
  )
);
const agencyCreci = computed(() => {
  const creci = firstValue(profile.value, ['creci'], '');
  const state = firstValue(profile.value, ['creci_uf'], '');
  return (
    [creci, state].filter(Boolean).join(' / ') || 'CRECI não disponibilizado'
  );
});
const agencyLocation = computed(() => {
  const address = profile.value?.endereco || {};
  const city = firstValue(address, ['cidade', 'city'], '');
  const state = firstValue(address, ['uf', 'state'], '');
  return (
    [city, state].filter(Boolean).join(', ') ||
    'Localização não disponibilizada'
  );
});
const teamCount = computed(
  () =>
    profile.value?.impact_summary?.corretores_count ??
    runtimeModel.records.value.length
);
const teamMembers = computed(() =>
  runtimeModel.records.value
    .map(record => ({
      avatar: imageUrl(
        firstValue(record.source, [
          'avatar_url',
          'thumbnail',
          'user.avatar_url',
          'user.thumbnail',
        ])
      ),
      id: record.id,
      initials: initialsFor(record.title),
      name: record.title || 'Nome não disponibilizado',
    }))
    .slice(0, 6)
);
const detailFacts = computed(() => runtimeModel.detailFacts.value);
const emptyHighlightSlots = computed(() =>
  Math.max(0, 7 - Math.max(detailFacts.value.length, 1))
);
const actionOptions = computed(() => runtimeModel.actionOptions.value);
const history = computed(() => runtimeModel.history.value || []);
const hasBroker = computed(() => Boolean(detail.value));
const unavailableMessage = computed(
  () =>
    'O backend atual não disponibiliza biografia, imagens, seguidores, avaliações, metas, conquistas ou feed deste corretor.'
);
const unavailableHistoryRows = [
  'Atualização não retornada pela API',
  'Atividade social indisponível',
  'Elogios não disponibilizados',
];
const unavailableBadgeSlots = [
  {
    icon: 'i-lucide-gem',
    label: 'Dados não retornados',
    tone: 'from-[#ffe1ed] to-[#f8c4df] text-[#c64492]',
  },
  {
    icon: 'i-lucide-award',
    label: 'Dados não retornados',
    tone: 'from-[#dfeaff] to-[#bdd9ff] text-[#4d79d6]',
  },
  {
    icon: 'i-lucide-star',
    label: 'Dados não retornados',
    tone: 'from-[#def8ea] to-[#bbeacc] text-[#3eaa78]',
  },
  {
    icon: 'i-lucide-badge-check',
    label: 'Dados não retornados',
    tone: 'from-[#ffefd7] to-[#f7cf93] text-[#d6882f]',
  },
  {
    icon: 'i-lucide-circle-slash-2',
    label: 'Dados não retornados',
    tone: 'from-[#edf0f5] to-[#dce1eb] text-[#8b95ad]',
  },
];
const unavailableRewardSlots = [
  {
    icon: 'i-lucide-trophy',
    label: 'Conquistas',
    detail: 'Não retornadas pela API',
  },
  {
    icon: 'i-lucide-badge-check',
    label: 'Reconhecimentos',
    detail: 'Não retornados pela API',
  },
  {
    icon: 'i-lucide-target',
    label: 'Metas',
    detail: 'Contrato não disponibilizado',
  },
  {
    icon: 'i-lucide-star',
    label: 'Avaliações',
    detail: 'Não retornadas pela API',
  },
];

const runPermittedAction = action => runtimeModel.runAction(action);
</script>

<template>
  <main class="w-full pb-3 pl-[30px] pr-[27px] pt-3 text-[#101958]">
    <section class="grid grid-cols-[minmax(0,1fr)_446px] items-start gap-6">
      <section class="min-w-0 space-y-2">
        <article
          data-imudei-visual-id="broker-profile-header"
          class="overflow-hidden rounded-2xl border border-[#e5e0eb] bg-white shadow-[0_4px_18px_rgba(49,29,90,0.04)]"
        >
          <div
            data-imudei-visual-id="broker-profile-hero"
            class="relative h-[184px] overflow-hidden bg-[linear-gradient(115deg,#151940_0%,#365a83_34%,#756677_63%,#db9278_100%)]"
          >
            <span
              class="absolute inset-0 bg-[radial-gradient(circle_at_21%_7%,rgba(120,192,255,0.42),transparent_29%),radial-gradient(circle_at_77%_10%,rgba(255,210,159,0.38),transparent_30%)]"
            />
            <div
              class="absolute inset-0 bg-gradient-to-t from-[#171140]/90 via-[#171140]/15 to-transparent"
            />
            <span
              class="absolute bottom-0 left-[9%] h-[96px] w-[82%] rounded-t-[72px] bg-[radial-gradient(ellipse_at_center_bottom,rgba(24,48,78,0.58)_0%,rgba(43,64,93,0.3)_42%,transparent_72%)]"
            />
            <span
              class="absolute inset-x-0 bottom-0 h-[76px] bg-[linear-gradient(90deg,rgba(10,19,51,0.56)_0%,rgba(36,44,83,0.26)_28%,rgba(32,47,79,0.14)_62%,rgba(34,22,53,0.68)_100%)]"
            />

            <div class="absolute -bottom-1 left-6 flex items-end gap-4">
              <span
                class="relative grid size-[136px] place-items-center rounded-full border-4 border-white bg-gradient-to-br from-[#f15aa5] via-[#7446e6] to-[#41c6e8] p-1 shadow-lg"
              >
                <img
                  v-if="brokerAvatar"
                  :src="brokerAvatar"
                  :alt="brokerName"
                  class="size-full rounded-full object-cover"
                />
                <span
                  v-else
                  class="relative grid size-full place-items-center overflow-hidden rounded-full bg-[radial-gradient(circle_at_30%_25%,#ffffff_0%,#e5dcff_42%,#c9dced_100%)] text-[#5a23c9]"
                >
                  <i class="i-lucide-user-round absolute top-5 size-12 text-white/70" />
                  <b class="relative text-[34px]">{{ initialsFor(brokerName) || '—' }}</b>
                </span>
                <i
                  class="absolute bottom-1 right-1 size-6 rounded-full border-4 border-white"
                  :class="hasBroker ? 'bg-emerald-500' : 'bg-[#a6afc4]'"
                />
              </span>

              <div class="min-w-0 pb-[37px] text-white">
                <div class="flex items-center gap-2">
                  <h1 class="truncate text-[25px] font-bold !text-white">
                    {{ brokerName }}
                  </h1>
                  <span
                    class="rounded-full bg-white/20 px-2 py-1 text-[8px] font-bold backdrop-blur"
                  >
                    {{ brokerVisibility }}
                  </span>
                </div>
                <p class="mt-1 text-[10px] font-semibold !text-white">
                  {{ brokerRole }}
                </p>
                <p
                  class="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[8px] !text-white/85"
                >
                  <span class="flex items-center gap-1">
                    <i class="i-lucide-id-card size-3" />
                    ID {{ brokerId }}
                  </span>
                  <span class="flex items-center gap-1">
                    <i class="i-lucide-building-2 size-3" />
                    {{ agencyName }}
                  </span>
                  <span class="flex items-center gap-1">
                    <i class="i-lucide-map-pin size-3" />
                    {{ agencyLocation }}
                  </span>
                </p>
              </div>
            </div>

            <div class="absolute right-4 top-6 flex gap-2">
              <button
                v-for="action in actionOptions"
                :key="action.name"
                class="h-9 rounded-lg bg-gradient-to-r from-[#6b20d9] to-[#db36c1] px-5 text-[9px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="realData.saving.value"
                type="button"
                @click="runPermittedAction(action.name)"
              >
                {{ realData.saving.value ? 'Processando…' : action.label }}
              </button>
              <button
                v-if="!actionOptions.length"
                class="h-9 rounded-lg bg-white/80 px-4 text-[9px] font-bold text-[#7d86a0]"
                disabled
                type="button"
              >
                Sem ações permitidas
              </button>
              <button
                class="grid size-9 place-items-center rounded-lg bg-white/80 text-[#7d86a0]"
                disabled
                title="Compartilhamento não disponibilizado pela API"
                type="button"
              >
                <i class="i-lucide-share size-4" />
              </button>
            </div>
          </div>

          <div
            class="grid min-h-[202px] grid-cols-[minmax(0,1fr)_352px] items-start gap-4 px-5 pb-2 pt-4"
          >
            <div>
              <p class="text-[10px] font-semibold">
                Detalhe operacional retornado pela agência.
              </p>
              <p class="mt-1 text-[9px] text-[#66739b]">
                {{ unavailableMessage }}
              </p>
              <div
                data-imudei-visual-id="broker-profile-tabs"
                class="mt-3 flex flex-wrap gap-2"
              >
                <span
                  class="rounded-full bg-[#f2ecff] px-3 py-1.5 text-[8px] font-semibold text-[#5b20d2]"
                >
                  {{ brokerVisibility }}
                </span>
                <span
                  class="rounded-full bg-[#f2ecff] px-3 py-1.5 text-[8px] font-semibold text-[#5b20d2]"
                >
                  {{ brokerListings }} anúncios ativos
                </span>
                <span
                  class="rounded-full bg-[#f2ecff] px-3 py-1.5 text-[8px] font-semibold text-[#5b20d2]"
                >
                  {{ brokerStatus }}
                </span>
              </div>

              <div class="mt-5 flex gap-7">
                <span v-if="!detailFacts.length" class="text-center">
                  <i
                    class="grid size-[52px] place-items-center rounded-full border-2 border-[#c7cce0] bg-[#faf9fd] text-[#8b95ad]"
                  >
                    <span class="i-lucide-circle-slash-2 size-4" />
                  </i>
                  <small
                    class="mt-1 block max-w-[58px] text-[6px] text-[#53608a]"
                  >
                    Sem destaques
                  </small>
                </span>
                <span
                  v-for="fact in detailFacts.slice(0, 7)"
                  :key="fact.label"
                  class="text-center"
                >
                  <i
                    class="grid size-[52px] place-items-center rounded-full border-2 border-[#ec56bd] bg-[radial-gradient(circle_at_30%_30%,#fff_0%,#efe5ff_36%,#d8edf8_100%)] text-[9px] font-bold text-[#5b20d2]"
                  >
                    {{ initialsFor(fact.label) || '•' }}
                  </i>
                  <small
                    class="mt-1 block max-w-[58px] truncate text-[6px] text-[#53608a]"
                  >
                    {{ fact.label }}
                  </small>
                </span>
                <span
                  v-for="index in emptyHighlightSlots"
                  :key="`unavailable-highlight-${index}`"
                  class="text-center"
                >
                  <i
                    class="grid size-[52px] place-items-center rounded-full border-2 border-[#ec56bd] bg-[radial-gradient(circle_at_30%_30%,#fff_0%,#efe5ff_38%,#d8edf8_100%)] text-[#8b95ad]"
                  >
                    <span class="i-lucide-image-off size-4" />
                  </i>
                  <small
                    class="mt-1 block max-w-[58px] truncate text-[6px] text-[#53608a]"
                  >
                    Indisponível
                  </small>
                </span>
              </div>
            </div>

            <div class="relative -mt-[64px]">
              <div
                class="grid grid-cols-3 divide-x divide-[#ece8f2] rounded-xl border border-[#e7e2ed] text-center"
              >
                <span class="px-3 py-5">
                  <small class="text-[7px] text-[#66739b]"
                    >Anúncios ativos</small
                  >
                  <strong class="mt-1 block text-[17px]">{{
                    brokerListings
                  }}</strong>
                  <b class="mt-1 block text-[7px] text-[#7c86a1]"
                    >Dado disponível</b
                  >
                </span>
                <span class="px-3 py-5">
                  <small class="text-[7px] text-[#66739b]">Vendas</small>
                  <strong class="mt-1 block text-[17px]">—</strong>
                  <b class="mt-1 block text-[7px] text-[#7c86a1]"
                    >Sem contrato</b
                  >
                </span>
                <span class="px-3 py-5">
                  <small class="text-[7px] text-[#66739b]">Comissão</small>
                  <strong class="mt-1 block text-[17px]">—</strong>
                  <b class="mt-1 block text-[7px] text-[#7c86a1]"
                    >Sem contrato</b
                  >
                </span>
              </div>

              <div class="mt-5 grid grid-cols-3 gap-2 text-center">
                <span class="rounded-xl border border-[#e7e2ed] px-2 py-3">
                  <small class="block text-[7px] text-[#66739b]">Status</small>
                  <b class="block truncate text-[9px] text-[#5b20d2]">{{
                    brokerStatus
                  }}</b>
                </span>
                <span class="rounded-xl border border-[#e7e2ed] px-2 py-3">
                  <small class="block text-[7px] text-[#66739b]">E-mail</small>
                  <b class="block truncate text-[8px]">{{
                    brokerEmail || '—'
                  }}</b>
                </span>
                <span class="rounded-xl border border-[#e7e2ed] px-2 py-3">
                  <small class="block text-[7px] text-[#66739b]"
                    >CRECI agência</small
                  >
                  <b class="block truncate text-[8px]">{{ agencyCreci }}</b>
                </span>
              </div>
            </div>
          </div>
        </article>

        <section
          data-imudei-visual-id="broker-profile-performance"
          class="grid h-[166px] grid-cols-[440px_348px_minmax(0,1fr)] gap-2 overflow-hidden"
        >
          <article class="rounded-2xl border border-[#e5e0eb] bg-white p-4">
            <header class="flex justify-between">
              <h2 class="text-[10px] font-bold">Desempenho indisponível</h2>
              <span
                class="rounded-lg border border-[#e8e3ef] px-2 py-1 text-[7px]"
              >
                Sem contrato
              </span>
            </header>
            <div class="mt-2 grid grid-cols-3 divide-x divide-[#eeeaf4]">
              <span class="px-3">
                <small class="text-[7px] text-[#66739b]">Vendas</small>
                <strong class="mt-1 block text-[12px]">—</strong>
                <b class="mt-1 block text-[7px] text-[#7c86a1]"
                  >Sem contrato</b
                >
              </span>
              <span class="px-3">
                <small class="text-[7px] text-[#66739b]">VGV</small>
                <strong class="mt-1 block text-[12px]">—</strong>
                <b class="mt-1 block text-[7px] text-[#7c86a1]"
                  >Sem contrato</b
                >
              </span>
              <span class="px-3">
                <small class="text-[7px] text-[#66739b]">Comissão</small>
                <strong class="mt-1 block text-[12px]">—</strong>
                <b class="mt-1 block text-[7px] text-[#7c86a1]"
                  >Sem contrato</b
                >
              </span>
            </div>
            <div
              class="relative mt-3 h-[42px] border-b border-dashed border-[#ddd6ea] text-center text-[#69759d]"
            >
              <small class="absolute inset-x-0 top-2 text-[7px]"
                >Série de desempenho não disponibilizada</small
              >
            </div>
          </article>

          <article class="rounded-2xl border border-[#e5e0eb] bg-white p-4">
            <header class="flex justify-between">
              <h2 class="text-[10px] font-bold">Meta mensal</h2>
              <span
                class="rounded-lg border border-[#e8e3ef] px-2 py-1 text-[7px]"
              >
                Sem contrato
              </span>
            </header>
            <div class="mt-3 flex items-center gap-4">
              <span
                class="grid size-[94px] shrink-0 place-items-center rounded-full border-[10px] border-[#ece8f4] text-center text-[#8a94ae]"
              >
                <b class="text-[19px]">—</b>
                <small class="block text-[6px] text-[#66739b]">Sem meta</small>
              </span>
              <dl class="flex-1 space-y-1 text-[8px]">
                <div>
                  <dt class="text-[#66739b]">VGV realizado</dt>
                  <dd class="font-bold">—</dd>
                </div>
                <div>
                  <dt class="text-[#66739b]">Meta</dt>
                  <dd class="font-bold">—</dd>
                </div>
                <div>
                  <dt class="text-[#66739b]">Falta</dt>
                  <dd class="font-bold">—</dd>
                </div>
                <small class="block pt-1 text-[7px] text-[#7c86a1]"
                  >Contrato de metas indisponível</small
                >
              </dl>
            </div>
          </article>
        </section>

        <section
          class="grid h-[168px] grid-cols-[1.19fr_0.95fr_0.66fr] gap-3 overflow-hidden"
        >
          <article class="rounded-2xl border border-[#e5e0eb] bg-white p-3">
            <header class="flex justify-between">
              <h2 class="text-[10px] font-bold">Portfólio de imóveis</h2>
              <span class="text-[7px] text-[#5b20d2]"
                >Lista indisponível</span
              >
            </header>
            <div class="mt-3 grid grid-cols-3 gap-2">
              <span
                v-for="index in 3"
                :key="`unavailable-listing-${index}`"
                class="overflow-hidden rounded-lg border border-[#eeeaf4] bg-white text-[#8b95ad]"
              >
                <span
                  class="grid h-[56px] place-items-center bg-gradient-to-br from-[#b5bfd7] via-[#d7c9d3] to-[#e9bd9d]"
                >
                  <i class="i-lucide-image-off size-4 text-white/90" />
                </span>
                <span class="block p-2">
                  <small class="text-[6px]">Imóvel indisponível</small>
                  <strong class="mt-1 block truncate text-[8px] text-[#66739b]"
                    >Sem dados da lista</strong
                  >
                  <small class="block truncate text-[6px]"
                    >A rota não retorna anúncios</small
                  >
                </span>
              </span>
            </div>
          </article>

          <article
            data-imudei-visual-id="broker-profile-feed"
            class="rounded-2xl border border-[#e5e0eb] bg-white p-3"
          >
            <header class="flex justify-between">
              <h2 class="text-[10px] font-bold">Feed</h2>
              <span class="text-[7px] text-[#5b20d2]">Indisponível</span>
            </header>
            <div v-if="history.length" class="mt-3 space-y-2">
              <p
                v-for="item in history.slice(0, 2)"
                :key="item.id"
                class="text-[7px] leading-3 text-[#526087]"
              >
                {{ item.label }}
              </p>
            </div>
            <div
              v-else
              class="mt-3 text-[#8b95ad]"
            >
              <span class="flex items-start gap-2">
                <i
                  class="grid size-8 shrink-0 place-items-center rounded-full bg-[#f2eff8] text-[#8b95ad]"
                >
                  <span class="i-lucide-rss size-4" />
                </i>
                <span class="min-w-0">
                  <strong class="block text-[8px] text-[#66739b]"
                    >Feed não disponibilizado</strong
                  >
                  <small class="mt-1 block text-[6px] leading-3"
                    >A rota não expõe atividades sociais deste corretor.</small
                  >
                </span>
              </span>
              <p class="mt-3 text-[7px] leading-3 text-[#66739b]">
                Não há publicação, comentário ou mídia retornados nesta rota.
              </p>
              <span
                class="mt-3 grid h-[96px] place-items-center rounded-lg border border-dashed border-[#d8d3e2] bg-gradient-to-br from-[#b9c5db] via-[#d7cfdb] to-[#ecd1af] text-white/90"
              >
                <i class="i-lucide-image-off size-4" />
              </span>
            </div>
          </article>

          <article class="rounded-2xl border border-[#e5e0eb] bg-white p-3">
            <header class="flex justify-between">
              <h2 class="text-[10px] font-bold">Seguidores e equipe</h2>
              <span class="text-[7px] text-[#5b20d2]">Dados parciais</span>
            </header>
            <strong class="mt-3 block text-[16px]">—</strong>
            <small class="text-[7px] text-[#66739b]"
              >Seguidores não disponibilizados</small
            >
            <div v-if="teamMembers.length" class="mt-2 flex -space-x-1">
              <i
                v-for="member in teamMembers"
                :key="member.id || member.name"
                class="grid size-7 place-items-center rounded-full border-2 border-white bg-violet-100 text-[6px] font-bold text-violet-700"
                :title="member.name"
              >
                <img
                  v-if="member.avatar"
                  :src="member.avatar"
                  :alt="member.name"
                  class="size-full rounded-full object-cover"
                />
                <template v-else>{{ member.initials || '—' }}</template>
              </i>
            </div>
            <p class="mt-2 text-[7px] font-bold">{{ agencyName }}</p>
            <small class="block text-[6px] text-[#66739b]">{{ teamCount }} corretores retornados</small>
            <div v-if="teamMembers.length" class="mt-2 flex -space-x-1">
              <i
                v-for="member in teamMembers.slice(0, 4)"
                :key="`agency-member-${member.id || member.name}`"
                class="grid size-6 place-items-center rounded-full border-2 border-white bg-[#f2eff8] text-[6px] font-bold text-violet-700"
                :title="member.name"
              >
                <img v-if="member.avatar" :src="member.avatar" :alt="member.name" class="size-full rounded-full object-cover" />
                <template v-else>{{ member.initials || '—' }}</template>
              </i>
            </div>
          </article>
        </section>

        <article
          data-imudei-visual-id="broker-profile-footer"
          class="grid h-[91px] grid-cols-4 overflow-hidden rounded-2xl bg-gradient-to-r from-[#5a20c8] via-[#d044bc] to-[#fff5f9] text-white"
        >
          <template v-if="detailFacts.length">
            <span
              v-for="fact in detailFacts.slice(0, 4)"
              :key="fact.label"
              class="flex items-center gap-3 border-r border-white/20 p-3 last:border-r-0"
            >
              <i
                class="grid size-9 place-items-center rounded-full bg-white/20 text-[12px]"
              >
                {{ initialsFor(fact.label) || '•' }}
              </i>
              <span>
                <strong class="block text-[8px]">{{ fact.label }}</strong>
                <small class="mt-1 block truncate text-[7px] opacity-80">{{
                  fact.value
                }}</small>
              </span>
            </span>
          </template>
          <template v-else>
            <span
              v-for="slot in unavailableRewardSlots"
              :key="slot.label"
              class="flex items-center gap-3 border-r border-white/20 p-3 last:border-r-0"
            >
              <i
                class="grid size-9 shrink-0 place-items-center rounded-full bg-white/20 text-[12px]"
              >
                <span :class="slot.icon" class="size-4" />
              </i>
              <span class="min-w-0">
                <strong class="block text-[8px]">{{ slot.label }}</strong>
                <small class="mt-1 block text-[7px] opacity-80">{{
                  slot.detail
                }}</small>
              </span>
            </span>
          </template>
        </article>
      </section>

      <aside
        data-imudei-visual-id="broker-profile-sidebar"
        class="space-y-[13px]"
      >
        <article
          class="h-[339px] rounded-2xl border border-[#e5e0eb] bg-white p-5"
        >
          <header class="flex justify-between">
            <h2 class="text-[11px] font-bold">Missões para ganhar pontos</h2>
            <span class="text-[8px] text-[#7c86a1]">Sem contrato</span>
          </header>
          <div class="mt-4">
            <div class="flex items-center gap-3">
              <i
                class="grid size-10 place-items-center rounded-full bg-violet-100 text-[#7c5ccf]"
              >
                <span class="i-lucide-badge-check size-4" />
              </i>
              <span class="min-w-0 flex-1">
                <strong class="block text-[9px]"
                  >Perfil completo e validado</strong
                >
                <small class="mt-1 block text-[7px] text-[#66739b]"
                  >Indisponível para este corretor.</small
                >
              </span>
              <b
                class="rounded-lg bg-[#f3edff] px-2 py-1 text-[8px] text-[#7c86a1]"
                >—</b
              >
            </div>
            <div class="ml-[52px] mt-2 h-1.5 rounded-full bg-[#e9e4f4]" />
          </div>
          <div class="mt-4">
            <div class="flex items-center gap-3">
              <i
                class="grid size-10 place-items-center rounded-full bg-blue-100 text-blue-500"
              >
                <span class="i-lucide-calendar-check-2 size-4" />
              </i>
              <span class="min-w-0 flex-1">
                <strong class="block text-[9px]"
                  >Atualizar disponibilidade</strong
                >
                <small class="mt-1 block text-[7px] text-[#66739b]"
                  >Indisponível para este corretor.</small
                >
              </span>
              <b
                class="rounded-lg bg-[#f3edff] px-2 py-1 text-[8px] text-[#7c86a1]"
                >—</b
              >
            </div>
            <div class="ml-[52px] mt-2 h-1.5 rounded-full bg-[#e9e4f4]" />
          </div>
          <div class="mt-4">
            <div class="flex items-center gap-3">
              <i
                class="grid size-10 place-items-center rounded-full bg-rose-100 text-rose-500"
              >
                <span class="i-lucide-target size-4" />
              </i>
              <span class="min-w-0 flex-1">
                <strong class="block text-[9px]">Atingir marco de meta</strong>
                <small class="mt-1 block text-[7px] text-[#66739b]"
                  >Indisponível para este corretor.</small
                >
              </span>
              <b
                class="rounded-lg bg-[#f3edff] px-2 py-1 text-[8px] text-[#7c86a1]"
                >—</b
              >
            </div>
            <div class="ml-[52px] mt-2 h-1.5 rounded-full bg-[#e9e4f4]" />
          </div>
          <p class="mt-4 rounded-lg bg-[#f7f3ff] p-3 text-[7px] text-[#5e4c9f]">
            A rota atual não retorna pontos, selos, metas ou a disponibilidade
            do corretor.
          </p>
        </article>

        <article
          class="h-[162px] overflow-hidden rounded-2xl border border-[#e5e0eb] bg-white p-5"
        >
          <header class="flex justify-between">
            <h2 class="text-[11px] font-bold">Selos conquistados</h2>
            <span class="text-[8px] text-[#7c86a1]">Indisponível</span>
          </header>
          <div class="mt-4 grid grid-cols-5 gap-3 text-center">
            <span v-for="slot in unavailableBadgeSlots" :key="slot.icon">
              <i
                class="mx-auto grid size-12 place-items-center rounded-2xl bg-gradient-to-br shadow-[inset_0_0_0_1px_rgba(255,255,255,0.72)]"
                :class="slot.tone"
              >
                <span :class="slot.icon" class="size-5" />
              </i>
              <strong class="mt-2 block text-[7px]">Sem dados</strong>
              <small class="mt-0.5 block text-[6px] text-[#8b95ad]">Não retornado</small>
            </span>
          </div>
        </article>

        <article
          class="h-[259px] overflow-hidden rounded-2xl border border-[#e5e0eb] bg-white p-5"
        >
          <header class="flex justify-between">
            <h2 class="text-[11px] font-bold">Atualizações recentes</h2>
            <span class="text-[8px] text-[#7c86a1]">Histórico</span>
          </header>
          <div v-if="history.length" class="mt-4 space-y-3">
            <div
              v-for="item in history.slice(0, 3)"
              :key="item.id"
              class="flex gap-3 border-b border-[#f0edf5] pb-3 last:border-0"
            >
              <span
                class="grid size-9 shrink-0 place-items-center rounded-full bg-violet-100 text-[8px] font-bold text-violet-700"
              >
                <i class="i-lucide-clock-3 size-4" />
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[8px]">{{
                  item.label
                }}</strong>
                <small class="mt-1 block text-[7px] text-[#66739b]">{{
                  item.date
                }}</small>
              </span>
            </div>
          </div>
          <div v-else class="mt-4 space-y-3">
            <div
              v-for="(item, index) in unavailableHistoryRows"
              :key="item"
              class="flex gap-3 border-b border-[#f0edf5] pb-3 last:border-0"
            >
              <span class="grid size-9 shrink-0 place-items-center rounded-full bg-[#f2eff8] text-[#8b95ad]">
                <i :class="index === 1 ? 'i-lucide-rss' : 'i-lucide-message-circle-off'" class="size-4" />
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block text-[8px] text-[#66739b]">{{ item }}</strong>
                <small class="mt-1 block text-[7px] text-[#8b95ad]">Sem conteúdo social na resposta atual.</small>
              </span>
              <small class="text-[7px] text-[#a0a8bb]">—</small>
            </div>
          </div>
        </article>
      </aside>
    </section>
  </main>
</template>
